import React, { useState } from "react"
import styled from "styled-components"
import { graphql } from "gatsby"
import Img from "gatsby-image"
import { useIntl } from "gatsby-plugin-intl"
import {
  FiArrowRight,
  FiArrowDown,
  FiArrowUpRight,
  FiLayers,
  FiActivity,
  FiCpu,
  FiCheck,
} from "react-icons/fi"
import Link from "../../components/Link"
import PageMetadata from "../../components/PageMetadata"
import { translateMessageId } from "../../utils/translations"

const Page = styled.div`
  width: 100%;
  color: ${({ theme }) => theme.colors.text};
  h1,
  h2,
  h3 {
    font-family: inherit;
    font-style: normal;
  }
  h2 {
    font-size: clamp(2rem, 3.8vw, 3.1rem);
    line-height: 1.12;
    letter-spacing: -0.04em;
    font-weight: 500;
    margin: 0 0 1.25rem;
  }
  h3 {
    font-weight: 500;
    letter-spacing: -0.025em;
  }
  a:focus-visible,
  button:focus-visible,
  select:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.primary};
    outline-offset: 5px;
  }
`
const Container = styled.div`
  width: min(100%, 1200px);
  margin: 0 auto;
  padding: 0 2.5rem;
  @media (max-width: 600px) {
    padding: 0 1.25rem;
  }
`
const Hero = styled.header`
  background: #081725;
  color: #fff;
  overflow: hidden;
  ${Container} {
    display: grid;
    grid-template-columns: 1.08fr 0.92fr;
    gap: 3.5rem;
    align-items: center;
    padding-block: 5rem;
  }
  h1 {
    color: #fff;
    font-size: clamp(3.2rem, 5.4vw, 5rem);
    line-height: 1.03;
    letter-spacing: -0.055em;
    font-weight: 500;
    margin: 0 0 1.75rem;
  }
  h1 span {
    display: block;
    color: #a9d9f5;
  }
  a:focus-visible {
    outline-color: #a9d9f5;
  }
  @media (max-width: 850px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2.5rem;
      padding-block: 3.5rem;
    }
    h1 {
      max-width: 700px;
      font-size: clamp(2.65rem, 8vw, 4.5rem);
    }
  }
`
const Kicker = styled.p`
  font-size: 0.7rem;
  text-transform: uppercase;
  letter-spacing: 0.13em;
  font-weight: 600;
  margin: 0 0 1.5rem;
  color: ${({ theme }) => theme.colors.primary};
`
const HeroKicker = styled(Kicker)`
  color: #b5d2e8;
  display: flex;
  align-items: center;
  gap: 0.85rem;
  &::before {
    content: "";
    width: 2rem;
    height: 2px;
    background: #a9d9f5;
    flex-shrink: 0;
  }
`
const Lead = styled.p`
  font-size: 1.08rem;
  line-height: 1.8;
  color: #c5d2df;
  max-width: 525px;
  margin: 0 0 2rem;
`
const Actions = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 1.25rem 1.75rem;
`
const PrimaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  background: #a9d9f5;
  border: 1px solid #a9d9f5;
  color: #0b1a28 !important;
  padding: 1rem 1.3rem;
  text-decoration: none;
  font-size: 0.95rem;
  font-weight: 600;
  &:hover {
    background: #ceeaff;
    border-color: #ceeaff;
  }
  svg {
    flex-shrink: 0;
  }
  &:hover svg {
    fill: none;
  }
`
const SecondaryLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0;
  color: #fff !important;
  border-bottom: 1px solid #8095a8;
  font-size: 0.9rem;
  text-decoration: none;
  &:hover {
    border-color: #fff;
  }
`
const HeroNote = styled.p`
  color: #a9bdcd;
  font-size: 0.8rem;
  line-height: 1.7;
  margin: 1.5rem 0 0;
`
const HeroVisual = styled.figure`
  margin: 0;
  min-width: 0;
  position: relative;
  border: 1px solid #425768;
  > .gatsby-image-wrapper {
    height: 430px;
  }
  &::after {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    background: linear-gradient(0deg, #06131af5, transparent 65%);
  }
  figcaption {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    z-index: 1;
    padding: 2rem;
    border-bottom: 3px solid #a9d9f5;
  }
  small {
    display: block;
    color: #a9d9f5;
    font-size: 0.65rem;
    letter-spacing: 0.12em;
    text-transform: uppercase;
    margin-bottom: 0.7rem;
  }
  strong {
    display: block;
    color: #fff;
    font-size: 1.65rem;
    font-weight: 500;
    line-height: 1.25;
    letter-spacing: -0.025em;
    max-width: 300px;
  }
  @media (max-width: 850px) {
    > .gatsby-image-wrapper {
      height: 340px;
    }
  }
  @media (max-width: 500px) {
    > .gatsby-image-wrapper {
      height: 280px;
    }
    figcaption {
      padding: 1.5rem;
    }
  }
`
const FocusStrip = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.ednBackground};
  ${Container} {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
  > div > div {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 1.75rem 1.4rem;
    border-right: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  > div > div:first-child {
    border-left: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  svg {
    width: 1.4rem;
    height: 1.4rem;
    color: ${({ theme }) => theme.colors.primary};
    flex-shrink: 0;
  }
  strong {
    display: block;
    font-size: 0.95rem;
    font-weight: 500;
  }
  small {
    display: block;
    font-size: 0.75rem;
    line-height: 1.6;
    color: ${({ theme }) => theme.colors.text200};
    margin-top: 0.25rem;
  }
  @media (max-width: 700px) {
    ${Container} {
      grid-template-columns: 1fr;
    }
    > div > div {
      padding: 1.2rem 0;
      border-right: 0;
      border-bottom: 1px solid ${({ theme }) => theme.colors.lightBorder};
    }
    > div > div:first-child {
      border-left: 0;
    }
    > div > div:last-child {
      border-bottom: 0;
    }
  }
`
const Catalog = styled.section`
  padding: 5rem 0;
  scroll-margin-top: 6rem;
  @media (max-width: 600px) {
    padding: 3rem 0;
  }
`
const Intro = styled.div`
  display: grid;
  grid-template-columns: 1fr 0.85fr;
  gap: 4rem;
  align-items: end;
  margin-bottom: 2.75rem;
  h2 {
    margin-bottom: 0;
    max-width: 550px;
  }
  > p {
    margin: 0;
    font-size: 1rem;
    line-height: 1.8;
    color: ${({ theme }) => theme.colors.text200};
  }
  @media (max-width: 800px) {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }
`
const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  > p {
    font-size: 0.8rem;
    margin: 0;
    color: ${({ theme }) => theme.colors.text200};
  }
`
const Filters = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  button {
    font: inherit;
    font-size: 0.85rem;
    cursor: pointer;
    border: 1px solid ${({ theme }) => theme.colors.lightBorder};
    color: ${({ theme }) => theme.colors.text};
    background: ${({ theme }) => theme.colors.background};
    padding: 0.8rem 1rem;
    display: inline-flex;
    align-items: center;
    gap: 0.75rem;
  }
  button:hover {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  button[aria-pressed="true"] {
    background: #0e2b44;
    color: #fff;
    border-color: #0e2b44;
  }
  span {
    font-size: 0.7rem;
    opacity: 0.8;
  }
  @media (max-width: 500px) {
    display: grid;
    width: 100%;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    button {
      justify-content: space-between;
      padding: 0.75rem;
      min-width: 0;
    }
  }
`
const ProductGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 2rem;
  @media (max-width: 700px) {
    grid-template-columns: 1fr;
    gap: 1.5rem;
  }
`
const ProductImage = styled(Img)`
  height: 100%;
  min-height: 240px;
  transition: transform 0.4s ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`
const ProductMedia = styled.div`
  height: 280px;
  overflow: hidden;
  background: #0c1c2a;
  position: relative;
  > span {
    position: absolute;
    top: 1.1rem;
    left: 1.1rem;
    border: 1px solid #ffffff55;
    background: #081725dd;
    color: #fff;
    font-size: 0.7rem;
    letter-spacing: 0.1em;
    padding: 0.4rem 0.6rem;
  }
  @media (max-width: 400px) {
    height: 225px;
  }
`
const Product = styled.article`
  position: relative;
  display: flex;
  flex-direction: column;
  border: 1px solid ${({ theme }) => theme.colors.lightBorder};
  background: ${({ theme }) => theme.colors.background};
  min-width: 0;
  transition: border-color 0.2s ease;
  &:hover,
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary};
  }
  &:hover ${ProductImage} {
    transform: scale(1.025);
  }
  ${({ $featured }) =>
    $featured &&
    `
    grid-column: 1 / -1;
    display: grid;
    grid-template-columns: 1fr 1fr;
    ${ProductMedia} { height: 100%; min-height: 350px; }
  `}
  @media (max-width: 700px) {
    display: flex;
    grid-column: auto;
    ${ProductMedia} {
      height: 250px;
      min-height: 0;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    &:hover ${ProductImage} {
      transform: none;
    }
  }
`
const ProductContent = styled.div`
  padding: 2rem;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  > p:first-child {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.65rem;
    text-transform: uppercase;
    letter-spacing: 0.12em;
    font-weight: 600;
    margin: 0 0 1rem;
  }
  h3 {
    font-size: clamp(1.6rem, 2.5vw, 2rem);
    line-height: 1.15;
    margin: 0 0 1rem;
  }
  > p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 1rem;
    line-height: 1.75;
    margin: 0 0 1.5rem;
  }
  ul {
    list-style: none;
    margin: 0 0 2rem;
    padding: 0;
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem 1rem;
  }
  li {
    display: flex;
    align-items: center;
    gap: 0.4rem;
    margin: 0;
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.75rem;
    line-height: 1.6;
  }
  li::before {
    display: none;
  }
  li svg {
    flex-shrink: 0;
    color: ${({ theme }) => theme.colors.primary};
  }
  @media (max-width: 500px) {
    padding: 1.5rem;
  }
`
const ProductLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  width: 100%;
  margin-top: auto;
  padding-top: 1.1rem;
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  font-size: 0.9rem;
  text-decoration: none;
  color: ${({ theme }) => theme.colors.text};
  font-weight: 600;
  &::after {
    content: "";
    position: absolute;
    inset: 0;
  }
  svg {
    flex-shrink: 0;
  }
  &:hover svg {
    fill: none;
  }
`
const Adoption = styled.section`
  padding: 4.5rem 0;
  background: ${({ theme }) => theme.colors.ednBackground};
  border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  ${Container} {
    display: grid;
    grid-template-columns: 0.9fr 1.1fr;
    gap: 4rem;
  }
  ol {
    list-style: none;
    margin: 0;
    padding: 0;
  }
  li {
    display: grid;
    grid-template-columns: 2rem 1fr;
    gap: 1.25rem;
    margin: 0 0 1.5rem;
    padding-top: 1.25rem;
    border-top: 1px solid ${({ theme }) => theme.colors.lightBorder};
  }
  li::before {
    display: none;
  }
  li > span {
    color: ${({ theme }) => theme.colors.primary};
    font-size: 0.75rem;
    margin-top: 0.25rem;
  }
  h3 {
    font-size: 1.2rem;
    margin: 0 0 0.5rem;
  }
  p {
    color: ${({ theme }) => theme.colors.text200};
    font-size: 0.95rem;
    line-height: 1.75;
    margin: 0;
  }
  @media (max-width: 800px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }
`
const Demo = styled.section`
  padding: 4.5rem 0;
  scroll-margin-top: 6rem;
  background: #081725;
  color: #fff;
  border-top: 3px solid #a9d9f5;
  ${Container} {
    display: grid;
    grid-template-columns: 1.1fr 0.9fr;
    gap: 4.5rem;
    align-items: center;
  }
  ${Kicker} {
    color: #a9d9f5;
    font-size: 0.7rem;
    margin: 0 0 1.5rem;
  }
  h2 {
    color: #fff;
  }
  p {
    color: #bdcddd;
    line-height: 1.8;
    font-size: 1rem;
    margin: 0;
  }
  label {
    display: block;
    color: #d4e2ed;
    font-size: 0.85rem;
    margin-bottom: 0.75rem;
  }
  select {
    display: block;
    width: 100%;
    padding: 1rem;
    border: 1px solid #607b92;
    background: #122b40;
    color: #fff;
    font: inherit;
    font-size: 0.95rem;
    margin-bottom: 1.25rem;
    border-radius: 0;
  }
  select:focus-visible,
  a:focus-visible {
    outline-color: #a9d9f5;
  }
  ${PrimaryLink} {
    width: 100%;
  }
  small {
    display: block;
    color: #aabed0;
    font-size: 0.75rem;
    line-height: 1.7;
    margin-top: 1rem;
  }
  @media (max-width: 800px) {
    ${Container} {
      grid-template-columns: 1fr;
      gap: 2rem;
    }
  }
`

const products = [
  { id: "pmo", categories: ["management"], to: "/productos/pmo-manager/" },
  {
    id: "devops",
    categories: ["observability"],
    to: "/productos/devops-monitor/",
  },
  {
    id: "devsecops",
    categories: ["observability", "ai"],
    to: "/productos/devsecops-monitor-ai/",
  },
  {
    id: "process",
    categories: ["ai"],
    to: "https://process-ai.newinntech.com",
    external: true,
  },
  { id: "gencode", categories: ["ai"], to: "/productos/gencode-ai/" },
  { id: "api", categories: ["observability"], to: "/productos/api-monitor/" },
  {
    id: "access",
    categories: ["management"],
    to: "/productos/control-acceso/",
  },
]
const categories = ["all", "management", "observability", "ai"]
const areas = [
  { id: "management", icon: FiLayers },
  { id: "observability", icon: FiActivity },
  { id: "ai", icon: FiCpu },
]

const ProductsPage = ({ data }) => {
  const intl = useIntl()
  const t = (id) => translateMessageId(`portfolio-${id}`, intl)
  const [category, setCategory] = useState("all")
  const [demoProduct, setDemoProduct] = useState("")
  const visibleProducts = products.filter(
    (product) => category === "all" || product.categories.includes(category)
  )
  const demoSubject = demoProduct
    ? `${t("demo-subject")}: ${t(`${demoProduct}-name`)}`
    : t("demo-subject")
  return (
    <Page>
      <PageMetadata
        title={t("meta-title")}
        description={t("meta-description")}
        image={data.gencode.childImageSharp.fixed.src}
      />
      <Hero>
        <Container>
          <div>
            <HeroKicker>{t("eyebrow")}</HeroKicker>
            <h1>
              {t("title")}
              <span>{t("title-accent")}</span>
            </h1>
            <Lead>{t("intro")}</Lead>
            <Actions>
              <PrimaryLink to="#catalogo">
                {t("explore")}
                <FiArrowDown aria-hidden="true" />
              </PrimaryLink>
              <SecondaryLink to="#demo">
                {t("demo-cta")}
                <FiArrowRight aria-hidden="true" />
              </SecondaryLink>
            </Actions>
            <HeroNote>{t("hero-note")}</HeroNote>
          </div>
          <HeroVisual>
            <Img
              fluid={data.gencode.childImageSharp.fluid}
              alt={t("gencode-alt")}
              loading="eager"
            />
            <figcaption>
              <small>{t("visual-kicker")}</small>
              <strong>{t("visual-title")}</strong>
            </figcaption>
          </HeroVisual>
        </Container>
      </Hero>
      <FocusStrip>
        <Container>
          {areas.map(({ id, icon: Icon }) => (
            <div key={id}>
              <Icon aria-hidden="true" />
              <div>
                <strong>{t(`area-${id}`)}</strong>
                <small>{t(`area-${id}-description`)}</small>
              </div>
            </div>
          ))}
        </Container>
      </FocusStrip>
      <Catalog id="catalogo" aria-labelledby="portfolio-catalog-title">
        <Container>
          <Intro>
            <div>
              <Kicker>{t("catalog-eyebrow")}</Kicker>
              <h2 id="portfolio-catalog-title">{t("catalog-title")}</h2>
            </div>
            <p>{t("catalog-intro")}</p>
          </Intro>
          <FilterBar>
            <Filters role="group" aria-label={t("filter-label")}>
              {categories.map((id) => (
                <button
                  key={id}
                  type="button"
                  aria-pressed={category === id}
                  aria-controls="portfolio-products"
                  onClick={() => setCategory(id)}
                >
                  {t(`filter-${id}`)}
                  <span aria-hidden="true">
                    {
                      products.filter(
                        (p) => id === "all" || p.categories.includes(id)
                      ).length
                    }
                  </span>
                </button>
              ))}
            </Filters>
            <p role="status" aria-live="polite" aria-atomic="true">
              {visibleProducts.length} {t("results")}
            </p>
          </FilterBar>
          <ProductGrid id="portfolio-products">
            {visibleProducts.map((product) => (
              <Product
                key={product.id}
                $featured={product.id === "pmo" && category === "all"}
                aria-labelledby={`portfolio-${product.id}-title`}
              >
                <ProductMedia>
                  <ProductImage
                    fluid={data[product.id].childImageSharp.fluid}
                    alt={t(`${product.id}-alt`)}
                  />
                  <span aria-hidden="true">
                    {String(products.indexOf(product) + 1).padStart(2, "0")}
                  </span>
                </ProductMedia>
                <ProductContent>
                  <p>{t(`${product.id}-category`)}</p>
                  <h3 id={`portfolio-${product.id}-title`}>
                    {t(`${product.id}-name`)}
                  </h3>
                  <p>{t(`${product.id}-description`)}</p>
                  <ul>
                    {[1, 2, 3].map((n) => (
                      <li key={n}>
                        <FiCheck aria-hidden="true" />
                        {t(`${product.id}-feature-${n}`)}
                      </li>
                    ))}
                  </ul>
                  <ProductLink
                    to={product.to}
                    hideArrow
                    ariaLabel={`${t(
                      product.external ? "visit" : "details"
                    )}: ${t(`${product.id}-name`)}`}
                  >
                    {t(product.external ? "visit" : "details")}
                    {product.external ? (
                      <FiArrowUpRight aria-hidden="true" />
                    ) : (
                      <FiArrowRight aria-hidden="true" />
                    )}
                  </ProductLink>
                </ProductContent>
              </Product>
            ))}
          </ProductGrid>
        </Container>
      </Catalog>
      <Adoption aria-labelledby="portfolio-adoption-title">
        <Container>
          <div>
            <Kicker>{t("adoption-eyebrow")}</Kicker>
            <h2 id="portfolio-adoption-title">{t("adoption-title")}</h2>
            <p>{t("adoption-intro")}</p>
          </div>
          <ol>
            {["choose", "validate", "plan"].map((id, i) => (
              <li key={id}>
                <span aria-hidden="true">0{i + 1}</span>
                <div>
                  <h3>{t(`step-${id}-title`)}</h3>
                  <p>{t(`step-${id}-description`)}</p>
                </div>
              </li>
            ))}
          </ol>
        </Container>
      </Adoption>
      <Demo id="demo" aria-labelledby="portfolio-demo-title">
        <Container>
          <div>
            <Kicker>{t("demo-eyebrow")}</Kicker>
            <h2 id="portfolio-demo-title">{t("demo-title")}</h2>
            <p>{t("demo-description")}</p>
          </div>
          <div>
            <label htmlFor="portfolio-demo-product">{t("demo-label")}</label>
            <select
              id="portfolio-demo-product"
              value={demoProduct}
              onChange={(event) => setDemoProduct(event.target.value)}
            >
              <option value="">{t("demo-default")}</option>
              {products.map((product) => (
                <option key={product.id} value={product.id}>
                  {t(`${product.id}-name`)}
                </option>
              ))}
            </select>
            <PrimaryLink
              to={`mailto:gerencia@newinntech.com?subject=${encodeURIComponent(
                demoSubject
              )}`}
              hideArrow
            >
              {t("demo-cta")}
              <FiArrowRight aria-hidden="true" />
            </PrimaryLink>
            <small>{t("demo-note")}</small>
          </div>
        </Container>
      </Demo>
    </Page>
  )
}
export default ProductsPage
export const query = graphql`
  query ProductsPortfolioPageQuery {
    pmo: file(relativePath: { eq: "products/pmo-editorial-v1.png" }) {
      ...PortfolioImage
    }
    devops: file(relativePath: { eq: "products/devops-editorial-v1.png" }) {
      ...PortfolioImage
    }
    devsecops: file(
      relativePath: { eq: "products/devsecops-editorial-v1.png" }
    ) {
      ...PortfolioImage
    }
    process: file(relativePath: { eq: "products/process-editorial-v1.png" }) {
      ...PortfolioImage
    }
    gencode: file(relativePath: { eq: "products/gencode-editorial-v1.png" }) {
      ...PortfolioImage
      childImageSharp {
        fixed(width: 1200) {
          src
        }
      }
    }
    api: file(relativePath: { eq: "products/api-editorial-v1.png" }) {
      ...PortfolioImage
    }
    access: file(relativePath: { eq: "products/access-editorial-v1.png" }) {
      ...PortfolioImage
    }
  }
  fragment PortfolioImage on File {
    childImageSharp {
      fluid(maxWidth: 1200, quality: 85) {
        ...GatsbyImageSharpFluid_withWebp
      }
    }
  }
`
